import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Lock, Plus, Smartphone, Unlock, Wallet } from 'lucide-react';

import { Button, Input, Modal } from '../../../components/ui';
import { useAuth } from '../../../app/auth';
import { creditoService } from '../../../services/creditoService';
import { Cliente, CreditoItem, HistorialRow, TipoMovimientoCredito } from '../../../types/clientes.types';

function formatColones(n: number | null | undefined): string {
  if (n == null) return '₡0';
  return `₡${Math.round(Number(n)).toLocaleString('es-CR', { maximumFractionDigits: 0 })}`;
}

function formatFecha(fecha?: string | null): string {
  if (!fecha) return '-';
  const d = new Date(fecha);
  return d.toLocaleString('es-CR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const TIPO_LABELS: Partial<Record<TipoMovimientoCredito, string>> = {
  CARGO_VENTA: 'Venta',
  CARGO_MANUAL: 'Crédito manual',
  ABONO_PAGO: 'Abono',
  PREMIO_DESCUENTO: 'Premio',
  AJUSTE: 'Ajuste',
};

function getTipoLabel(row: HistorialRow): string {
  if (row.tipo === 'ABONO_PAGO') {
    if (row.referencia_tipo === 'sinpe') return 'Abono SINPE';
    if (row.referencia_tipo === 'transferencia') return 'Abono Transf.';
    if (row.referencia_tipo === 'efectivo') return 'Abono efectivo';
    return 'Abono';
  }
  return TIPO_LABELS[row.tipo] || row.tipo;
}

type ModalType = 'CARGO' | 'ABONO' | null;

type TipoPago = 'efectivo' | 'sinpe' | 'transferencia';

interface MovimientoModalProps {
  type: ModalType;
  cliente: Cliente;
  creditoSeleccionado: CreditoItem | null;
  onClose: () => void;
  onSuccess: () => void;
  initialTipoPago?: TipoPago;
}

const MovimientoModal: React.FC<MovimientoModalProps> = ({
  type,
  cliente,
  creditoSeleccionado,
  onClose,
  onSuccess,
  initialTipoPago = 'efectivo',
}) => {
  const [monto, setMonto] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [tipoPago, setTipoPago] = useState<TipoPago>(initialTipoPago);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!type) return;
    setMonto('');
    setDescripcion('');
    setError('');
    setTipoPago(initialTipoPago);
  }, [type, creditoSeleccionado, initialTipoPago]);

  if (!type) return null;

  const titulo = type === 'CARGO' ? 'Nuevo crédito' : 'Registrar abono';

  const handleSubmit = async () => {
    const montoNum = Number(monto);
    if (!Number.isFinite(montoNum) || montoNum <= 0) {
      setError('Monto debe ser mayor a cero');
      return;
    }
    if (!descripcion.trim()) {
      setError('Descripción obligatoria');
      return;
    }
    if (type === 'ABONO' && !creditoSeleccionado) {
      setError('Seleccione un crédito');
      return;
    }

    setLoading(true);
    setError('');
    try {
      if (type === 'CARGO') {
        await creditoService.crearCredito({
          cliente_id: cliente.id,
          monto_total: montoNum,
          descripcion: descripcion.trim(),
        });
      } else if (creditoSeleccionado) {
        await creditoService.abonarCredito(creditoSeleccionado.id, {
          monto: montoNum,
          descripcion: descripcion.trim(),
          tipo_pago: tipoPago,
        });
      }
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'No se pudo registrar la operación');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={titulo} size="sm">
      <div className="space-y-4">
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="font-medium text-gray-900">{cliente.nombre}</p>
          <p className="text-sm text-gray-500">Saldo total: {formatColones(cliente.saldo_pendiente)}</p>
          {creditoSeleccionado && type === 'ABONO' && (
            <p className="text-sm text-gray-500 mt-1">
              Crédito: {formatColones(creditoSeleccionado.saldo_actual)} pendiente
            </p>
          )}
        </div>

        {type === 'ABONO' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de pago</label>
            <div className="flex gap-2">
              {(['efectivo', 'sinpe', 'transferencia'] as TipoPago[]).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTipoPago(t)}
                  className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                    tipoPago === t
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {t === 'efectivo' ? '💵 Efectivo' : t === 'sinpe' ? '📲 SINPE' : '🏦 Transf.'}
                </button>
              ))}
            </div>
          </div>
        )}

        <Input
          label="Monto"
          type="number"
          value={monto}
          onChange={(e) => setMonto(e.target.value)}
          min={0}
          step={100}
          autoFocus
        />

        <Input
          label="Descripción"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          placeholder={type === 'CARGO' ? 'Ej: Crédito de mercadería' : 'Ej: Abono en caja local'}
        />

        {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}

        <div className="flex gap-2 pt-2">
          <Button variant="secondary" onClick={onClose} className="flex-1">Cancelar</Button>
          <Button
            variant={type === 'CARGO' ? 'warning' : 'success'}
            onClick={handleSubmit}
            isLoading={loading}
            className="flex-1"
          >
            Guardar
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export const ClienteCreditoPage: React.FC = () => {
  const { clienteId } = useParams<{ clienteId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [historial, setHistorial] = useState<HistorialRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalType, setModalType] = useState<ModalType>(null);
  const [modalTipoPago, setModalTipoPago] = useState<TipoPago>('efectivo');
  const [creditoSeleccionado, setCreditoSeleccionado] = useState<CreditoItem | null>(null);

  const isLocal = user?.role === 'LOCAL';
  const isVendedor = user?.role === 'VENDEDOR_CON_CAJA';

  const loadData = useCallback(async () => {
    if (!clienteId) return;
    setLoading(true);
    setError('');
    try {
      const [clienteData, historialData] = await Promise.all([
        creditoService.obtenerCliente(clienteId),
        creditoService.obtenerHistorial(clienteId),
      ]);
      setCliente(clienteData);
      setHistorial(historialData);
      const primerActivo = (clienteData.creditos || []).find(
        (credito) => credito.estado === 'ACTIVO' && credito.saldo_actual > 0,
      ) || null;
      setCreditoSeleccionado(primerActivo);
    } catch (err: any) {
      setError(err.message || 'Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleBloqueo = async () => {
    if (!cliente) return;
    try {
      const actualizado = await creditoService.toggleBloqueo(cliente.id, !!cliente.bloqueado_credito);
      setCliente(actualizado);
    } catch (err: any) {
      setError(err.message || 'No se pudo actualizar el cliente');
    }
  };

  const creditosActivos = useMemo(
    () => (cliente?.creditos || []).filter((credito) => credito.estado === 'ACTIVO' && credito.saldo_actual > 0),
    [cliente],
  );

  const onSuccess = () => {
    setModalType(null);
    loadData();
  };

  const abrirAbono = (tipoPago: TipoPago = 'efectivo', credito?: CreditoItem) => {
    if (credito) setCreditoSeleccionado(credito);
    setModalTipoPago(tipoPago);
    setModalType('ABONO');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error && !cliente) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error}</p>
        <Button variant="primary" onClick={() => navigate(-1)}>Volver</Button>
      </div>
    );
  }

  if (!cliente) return null;

  return (
    <div className="bg-white min-h-full space-y-6">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-lg font-bold text-gray-900">{cliente.nombre}</h1>
            <p className="text-sm text-gray-500">Saldo total: {formatColones(cliente.saldo_pendiente)}</p>
          </div>
          {cliente.bloqueado_credito && (
            <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">BLOQUEADO</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isLocal && (
            <Button
              variant="secondary"
              leftIcon={cliente.bloqueado_credito ? <Unlock size={16} /> : <Lock size={16} />}
              onClick={toggleBloqueo}
            >
              {cliente.bloqueado_credito ? 'Desbloquear' : 'Bloquear'}
            </Button>
          )}
          {isVendedor && !cliente.bloqueado_credito && (
            <Button variant="warning" leftIcon={<Plus size={16} />} onClick={() => setModalType('CARGO')}>
              Nuevo crédito
            </Button>
          )}
          {isVendedor && !cliente.bloqueado_credito && creditosActivos.length > 0 && (
            <>
              <Button variant="success" leftIcon={<Wallet size={16} />} onClick={() => abrirAbono('efectivo')}>
                Abonar
              </Button>
              <Button variant="primary" leftIcon={<Smartphone size={16} />} onClick={() => abrirAbono('sinpe')}>
                SINPE / Transf.
              </Button>
            </>
          )}
        </div>
      </div>

      {error && <p className="px-4 text-sm text-danger-600">{error}</p>}

      <div className="px-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-gray-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Wallet size={18} className="text-primary-600" />
            <h2 className="font-semibold text-gray-900">Créditos del cliente</h2>
          </div>
          <div className="space-y-3">
            {(cliente.creditos || []).length === 0 && (
              <p className="text-sm text-gray-500">No hay créditos registrados.</p>
            )}
            {(cliente.creditos || []).map((credito) => (
              <div
                key={credito.id}
                className={`rounded-lg border p-3 ${creditoSeleccionado?.id === credito.id ? 'border-primary-400 bg-primary-50' : 'border-gray-200'}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <p className="font-medium text-gray-900">{credito.descripcion || 'Crédito'}</p>
                    <p className="text-xs text-gray-500">{formatFecha(credito.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Total {formatColones(credito.monto_total)}</p>
                    <p className="font-bold text-gray-900">Pendiente {formatColones(credito.saldo_actual)}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${credito.estado === 'ACTIVO' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                    {credito.estado}
                  </span>
                  <div className="flex gap-2">
                    {isLocal && credito.estado === 'ACTIVO' && credito.saldo_actual > 0 && (
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => {
                          setCreditoSeleccionado(credito);
                          setModalType('ABONO');
                        }}
                      >
                        Abonar
                      </Button>
                    )}
                    {isVendedor && credito.estado === 'ACTIVO' && credito.saldo_actual > 0 && (
                      <>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => abrirAbono('efectivo', credito)}
                        >
                          Abonar
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => abrirAbono('sinpe', credito)}
                        >
                          SINPE
                        </Button>
                      </>
                    )}
                    {credito.estado === 'ACTIVO' && (
                      <Button variant="secondary" size="sm" onClick={() => setCreditoSeleccionado(credito)}>
                        Seleccionar
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-gray-200 rounded-xl p-4 overflow-auto">
          <h2 className="font-semibold text-gray-900 mb-3">Historial</h2>
          {historial.length === 0 ? (
            <p className="text-sm text-gray-500">No hay movimientos registrados.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-gray-200 text-gray-500">
                  <th className="py-2">Fecha</th>
                  <th className="py-2">Tipo</th>
                  <th className="py-2">Descripción</th>
                  <th className="py-2 text-right">Cargo</th>
                  <th className="py-2 text-right">Abono</th>
                  <th className="py-2 text-right">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {historial.map((row) => (
                  <tr key={row.id} className="border-b border-gray-100">
                    <td className="py-2 pr-2 whitespace-nowrap">{formatFecha(row.fecha)}</td>
                    <td className="py-2 pr-2">{getTipoLabel(row)}</td>
                    <td className="py-2 pr-2">{row.descripcion || '-'}</td>
                    <td className="py-2 text-right">{row.cargo ? formatColones(row.cargo) : '-'}</td>
                    <td className="py-2 text-right">{row.abono ? formatColones(row.abono) : '-'}</td>
                    <td className="py-2 text-right font-semibold">{formatColones(row.saldo_acumulado)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {modalType && (
        <MovimientoModal
          type={modalType}
          cliente={cliente}
          creditoSeleccionado={creditoSeleccionado || creditosActivos[0] || null}
          onClose={() => setModalType(null)}
          onSuccess={onSuccess}
          initialTipoPago={modalTipoPago}
        />
      )}
    </div>
  );
};

export default ClienteCreditoPage;
